/**
 * ⭐⭐ **O TÓPICO ATOS NOTARIAIS NO DESENHO DE IMÓVEIS — 18/09/2026.**
 *
 * O terceiro da fila do pedido dele (PRD §46): resumo no alto, uma parte por
 * vez, os botões, a faixa do que foi pesquisado para a parte, uma linha por ato
 * que abre o detalhe.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** A relação da central
 * (`PesquisaDaCentral`: subir o PDF, registrar só a pesquisa, a lista lida,
 * pedir cópia), a busca automática (`BuscaNaCentral`, com o custo à vista), a
 * ficha de lançar (`FichaDoAtivo`), os gravames, o aceite, corrigir e apagar
 * são as mesmas peças de antes — importadas, não copiadas.
 *
 * ⚠️ **Mudou o LUGAR da relação e da busca:** eram blocos de todas as partes no
 * alto do tópico, sempre abertos; agora são da parte escolhida, e abrem num
 * painel pelo botão — como "Ler matrícula em PDF" em Imóveis. O que a relação
 * diz aparece na faixa, pelo estado que a trilha já calcula (feito, nada consta,
 * aguardando cópia há N dias).
 */
import { useEffect, useState } from 'react';
import {
  apiInvestigados,
  apiTrilha,
  QUALIFICACOES,
  type AtivoDoInvestigado,
  type GravamesDoTopico,
  type Investigado,
  type LinhaDaTrilha,
} from '../lib/api';
import { destinoDoBem } from '../lib/efeito-do-ato';
import { usarEhMaster } from '../lib/papel';
import { avisar, avisarErro } from './Avisos';
import { Confirmar } from './Confirmar';
import {
  BuscaNaCentral,
  FichaDoAtivo,
  FormularioDeOnus,
  PesquisaDaCentral,
  ROTULO,
  ROTULO_DO_EFEITO,
  ROTULO_DO_PAPEL,
  dinheiro,
} from './AtivosLocalizados';
import { iniciais, Traco } from './ImoveisDoCaso';
import { dizer } from './TrilhaDaApuracao';
import '../estilos/imoveis-2026.css';

const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
/* Só a primeira letra: "Aguardando cópia há 3 dia(s)" vira "aguardando cópia…". */
const minuscula = (t: string) => t.charAt(0).toLocaleLowerCase('pt-BR') + t.slice(1);
const ativoGravame = (o: { situacao: string | null }) => (o.situacao ?? '').trim().toUpperCase() === 'ATIVO';
const PESQUISADA = new Set(['feito', 'nada_consta', 'aguardando', 'a_confirmar']);

/** De onde vem cada ato — dito ao passar o mouse na marca da faixa. */
const FONTE_DA_CENTRAL =
  'A pesquisa na central notarial é humana hoje: você pesquisa, sobe o PDF da relação (ou registra só a pesquisa) e ' +
  'lança aqui o que achou — escrituras e procurações vêm na mesma relação.';

type Dados = { ativos: AtivoDoInvestigado[]; gravames: GravamesDoTopico | null };
type Painel = null | 'central' | 'busca';

export function AtosDoCaso({ casoId, chave }: { casoId: string; chave: string }) {
  const ehMaster = usarEhMaster();
  const [partes, setPartes] = useState<Investigado[] | null>(null);
  const [porParte, setPorParte] = useState<Record<string, Dados>>({});
  const [trilha, setTrilha] = useState<Record<string, LinhaDaTrilha[]>>({});
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [revisao, setRevisao] = useState(0);
  const [painel, setPainel] = useState<Painel>(null);
  const [ficha, setFicha] = useState<null | { corrigindo: AtivoDoInvestigado | null }>(null);

  useEffect(() => {
    let vivo = true;
    void apiInvestigados
      .listar(casoId)
      .then((i) => {
        if (!vivo) return;
        setPartes(i.investigados);
        setEscolhida((atual) => atual ?? i.investigados[0]?.id ?? null);
      })
      .catch(() => vivo && setPartes([]));
    return () => {
      vivo = false;
    };
  }, [casoId, chave]);

  useEffect(() => {
    if (!partes) return;
    let vivo = true;
    void Promise.all(
      partes.map((p) =>
        apiInvestigados
          .ativos(p.id)
          .then((r) => [p.id, { ativos: r.ativos.filter((a) => a.categoria === 'atos'), gravames: r.porCategoria?.atos ?? null }] as const)
          .catch(() => [p.id, { ativos: [], gravames: null }] as const),
      ),
    ).then((pares) => {
      if (vivo) setPorParte(Object.fromEntries(pares));
    });
    /* A faixa lê o estado que a trilha já calcula — só leitura, nada consulta. */
    void apiTrilha
      .doCaso(casoId)
      .then((t) => vivo && setTrilha(Object.fromEntries(t.partes.map((p) => [p.investigadoId, p.linhas]))))
      .catch(() => vivo && setTrilha({}));
    return () => {
      vivo = false;
    };
  }, [partes, revisao, casoId]);

  if (partes === null) return <p className="ajuda">Carregando…</p>;
  if (partes.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Este caso ainda não tem parte investigada cadastrada. Os atos se lançam por pessoa — cadastre os investigados
        no caso primeiro.
      </p>
    );
  }

  const parte = partes.find((p) => p.id === escolhida) ?? partes[0]!;
  const dados = porParte[parte.id];
  const todos = Object.values(porParte).flatMap((d) => d.ativos);
  const aoMudar = () => setRevisao((n) => n + 1);
  const linhaDe = (id: string) => (trilha[id] ?? []).find((l) => l.chave === 'atos');
  const central = linhaDe(parte.id);
  const pesquisadas = partes.filter((p) => PESQUISADA.has(linhaDe(p.id)?.palavra ?? '')).length;
  const aguardando = partes.filter((p) => linhaDe(p.id)?.palavra === 'aguardando').length;

  return (
    <div className="imv">
      <Resumo ativos={todos} pesquisadas={pesquisadas} partes={partes.length} aguardando={aguardando} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {partes.map((p) => {
          const n = porParte[p.id]?.ativos.length ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                if (ficha) {
                  avisar('Lance ou cancele o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
                setPainel(null);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span className={`imv-conta${n === 0 ? ' zero' : ''}`} title={n === 0 ? 'nada lançado' : undefined}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {ficha ? (
        <FichaDoAtivo
          key={ficha.corrigindo?.id ?? 'novo'}
          investigado={parte}
          categoria="atos"
          corrigindo={ficha.corrigindo}
          aoFechar={() => setFicha(null)}
          aoGravar={() => {
            setFicha(null);
            aoMudar();
          }}
        />
      ) : (
        <>
          <div className="imv-acoes">
            <button
              type="button"
              className="imv-bt primario"
              onClick={() => {
                setPainel(null);
                setFicha({ corrigindo: null });
              }}
            >
              <Traco d={['M12 5v14', 'M5 12h14']} />
              Lançar ato notarial
            </button>
            {/* ⚠️ Só master, como antes: a relação e o pedido de cópias gastam do Fundo de Custas. */}
            {ehMaster && (
              <button
                type="button"
                className={`imv-bt${painel === 'central' ? ' aberto' : ''}`}
                aria-expanded={painel === 'central'}
                onClick={() => setPainel(painel === 'central' ? null : 'central')}
              >
                <Traco d={['M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8z', 'M14 3.5V8h4.5', 'M9 12.5h6', 'M9 16h4']} />
                Relação da central
              </button>
            )}
          </div>

          <div className="imv-fontes">
            <span className="imv-rot">Pesquisado para esta parte</span>
            <span className={`imv-fonte${PESQUISADA.has(central?.palavra ?? '') ? ' ok' : ' falta'}`} title={FONTE_DA_CENTRAL}>
              <i />
              Central notarial · {central ? minuscula(dizer(central)) : 'em branco'}
            </span>
            <span className="imv-esp" />
            {ehMaster && (
              <button
                type="button"
                className={`imv-bt fantasma p${painel === 'busca' ? ' aberto' : ''}`}
                aria-expanded={painel === 'busca'}
                onClick={() => setPainel(painel === 'busca' ? null : 'busca')}
              >
                <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
                Busca automática
              </button>
            )}
          </div>

          {painel === 'central' && ehMaster && (
            <div className="imv-painel">
              <PesquisaDaCentral investigado={parte} categoria="atos" />
            </div>
          )}
          {painel === 'busca' && ehMaster && (
            <div className="imv-painel">
              <BuscaNaCentral lista={[parte]} />
            </div>
          )}

          <ListaDeAtos
            dados={dados}
            investigadoId={parte.id}
            ehMaster={ehMaster}
            aoMudar={aoMudar}
            aoCorrigir={(a) => {
              setPainel(null);
              setFicha({ corrigindo: a });
            }}
          />
        </>
      )}
    </div>
  );
}

/* ── O resumo do caso ─────────────────────────────────────────────────────── */
function Resumo({
  ativos,
  pesquisadas,
  partes,
  aguardando,
}: {
  ativos: AtivoDoInvestigado[];
  pesquisadas: number;
  partes: number;
  aguardando: number;
}) {
  const escrituras = ativos.filter((a) => a.classificacao === 'escritura');
  /* ⭐ PRD §65 (27/10/2026): na escritura, o papel do investigado decide o lado. */
  const saiu = escrituras.filter((a) => a.efeitoPatrimonial && destinoDoBem(a.efeitoPatrimonial, a.papelNoAto) === 'saiu').length;
  const procuracoes = ativos.filter((a) => a.classificacao === 'procuracao').length;
  const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
  return (
    <div className="imv-resumo">
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Escrituras</span>
        <strong>{escrituras.length}</strong>
        <small>{saiu === 0 ? 'nenhuma tirou patrimônio' : `${plural(saiu, 'tirou', 'tiraram')} patrimônio`}</small>
      </div>
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Procurações</span>
        <strong>{procuracoes}</strong>
        <small>poder não é ato praticado</small>
      </div>
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Partes pesquisadas</span>
        <strong>
          {pesquisadas} de {partes}
        </strong>
        <small>na central notarial</small>
      </div>
      <div className="imv-kpi k-sem">
        <span className="imv-rot">Aguardando cópia</span>
        <strong>{aguardando}</strong>
        <small>{aguardando === 1 ? 'parte com certidão pedida' : 'partes com certidão pedida'}</small>
      </div>
    </div>
  );
}

/* ── A lista ──────────────────────────────────────────────────────────────── */
function ListaDeAtos({
  dados,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  dados: Dados | undefined;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aberto, setAberto] = useState<string | null>(null);
  if (!dados) return <p className="ajuda">Carregando…</p>;
  const g = dados.gravames;
  /* Escrituras antes das procurações; dentro, em ordem de data. */
  const ordem = [...dados.ativos].sort(
    (x, y) =>
      (x.classificacao === 'escritura' ? 0 : 1) - (y.classificacao === 'escritura' ? 0 : 1) ||
      (x.ocorridoEm ?? '9999').localeCompare(y.ocorridoEm ?? '9999'),
  );
  return (
    <>
      <div className="imv-lista">
        <div className="imv-grade imv-cab">
          <span className="imv-rot">Espécie</span>
          <span className="imv-rot">Ato</span>
          <span className="imv-rot">Data e partes</span>
          <span className="imv-rot dir">Valor declarado</span>
          <span />
        </div>
        {ordem.length === 0 ? (
          /* ⚠️ Vazio diz "nada lançado" e NÃO some: ausência de ato não é ausência de pesquisa. */
          <p className="imv-vazio">Nada lançado para esta parte.</p>
        ) : (
          ordem.map((a) => (
            <LinhaDoAto
              key={a.id}
              ativo={a}
              aberto={aberto === a.id}
              aoAbrir={() => setAberto(aberto === a.id ? null : a.id)}
              investigadoId={investigadoId}
              ehMaster={ehMaster}
              aoMudar={aoMudar}
              aoCorrigir={aoCorrigir}
            />
          ))
        )}
      </div>
      <div className="imv-legenda">
        <span className="imv-legenda-longa">
          <span className="imv-info">i</span> Atos não somam ao patrimônio: a escritura é evidência de movimentação, e a
          procuração não transfere propriedade
        </span>
        {g && g.exposicaoQuantos > 0 && (
          <span>
            <span className="imv-info">i</span> Gravames ativos: {g.exposicaoQuantos}
            {g.exposicao > 0 && <> · somam {dinheiro(g.exposicao)}</>}
            {g.exposicaoSemValor > 0 && <> · {g.exposicaoSemValor} sem valor no papel</>} — valor do ato, não saldo
            devedor; <strong>não se subtraem</strong> do patrimônio
          </span>
        )}
      </div>
    </>
  );
}

function LinhaDoAto({
  ativo,
  aberto,
  aoAbrir,
  investigadoId,
  ehMaster,
  aoMudar,
  aoCorrigir,
}: {
  ativo: AtivoDoInvestigado;
  aberto: boolean;
  aoAbrir: () => void;
  investigadoId: string;
  ehMaster: boolean;
  aoMudar: () => void;
  aoCorrigir: (a: AtivoDoInvestigado) => void;
}) {
  const [aApagar, setAApagar] = useState(false);
  /* ⛔ Apagar um gravame pergunta antes — a regra dele, 18/09/2026. */
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);
  const gravamesAtivos = ativo.onus.filter(ativoGravame);
  const escritura = ativo.classificacao === 'escritura';
  const destino = ativo.efeitoPatrimonial ? destinoDoBem(ativo.efeitoPatrimonial, ativo.papelNoAto) : null;
  const papel = ativo.papelNoAto ? `o investigado é ${ROTULO_DO_PAPEL[ativo.papelNoAto].split(' — ')[0]!.toLocaleLowerCase('pt-BR')}` : null;
  const outra = escritura
    ? ativo.papelNoAto === 'vendedor' || ativo.papelNoAto === 'doador' || ativo.papelNoAto === 'cedente'
      ? ativo.adquirente
      : ativo.transmitente
    : null;

  return (
    <article className={`imv-linha${aberto ? ' aberta' : ''}`}>
      <button type="button" className="imv-grade imv-linha-topo" onClick={aoAbrir} aria-expanded={aberto}>
        <span className="imv-sit-coluna">
          <span className="imv-sit terceiro">
            <i />
            {escritura ? 'Escritura' : 'Procuração'}
          </span>
          {destino === 'saiu' && <span className="imv-tag cinza">o bem saiu neste ato</span>}
          {gravamesAtivos.length > 0 && (
            <span className="imv-tag">
              {gravamesAtivos.length} gravame{gravamesAtivos.length === 1 ? '' : 's'} ativo{gravamesAtivos.length === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="imv-bem">
          <b>{ativo.descricao}</b>
          {ativo.identificacao && <span className="imv-mono">{ativo.identificacao}</span>}
          {ativo.tabelionato && <small>{ativo.tabelionato}</small>}
        </span>
        <span className="imv-ato">
          <b>{emData(ativo.ocorridoEm) || '—'}</b>
          {(papel || outra) && <small>{[papel, outra ? `com ${outra}` : null].filter(Boolean).join(' · ')}</small>}
        </span>
        <span className="imv-valor">
          {ativo.valorDeclarado !== null ? (
            <>
              <b>{dinheiro(ativo.valorDeclarado)}</b>
              <small>no ato · não é avaliação</small>
            </>
          ) : (
            <>
              <b className="na">—</b>
              <small>{escritura ? 'não declarado' : 'procuração'}</small>
            </>
          )}
        </span>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>

      {aberto && (
        <div className="imv-detalhe">
          <div>
            <h5 className="imv-rot">O ato</h5>
            <ul className="imv-mini">
              <li>
                <span>{ROTULO[ativo.classificacao]}{ativo.ocorridoEm ? ` · ${emData(ativo.ocorridoEm)}` : ''}</span>
              </li>
              {ativo.tabelionato && (
                <li>
                  <span>{ativo.tabelionato}</span>
                </li>
              )}
              {ativo.identificacao && (
                <li>
                  <span>{ativo.identificacao}</span>
                </li>
              )}
              {ativo.papelNoAto && (
                <li>
                  <span>O investigado é {ROTULO_DO_PAPEL[ativo.papelNoAto]}</span>
                </li>
              )}
              {ativo.efeitoPatrimonial && (
                <li>
                  <span>
                    {ROTULO_DO_EFEITO[ativo.efeitoPatrimonial]}
                    {destino === 'saiu'
                      ? ' — o bem saiu do patrimônio neste ato'
                      : destino === 'a_conferir'
                        ? ' — confira o lado do investigado: o bem pode ter saído ou entrado neste ato'
                        : ''}
                  </span>
                </li>
              )}
            </ul>
          </div>

          {escritura ? (
            <div>
              <h5 className="imv-rot">As partes</h5>
              <ul className="imv-mini">
                {ativo.transmitente && (
                  <li>
                    <span>De {ativo.transmitente}</span>
                  </li>
                )}
                {ativo.adquirente && (
                  <li>
                    <span>
                      Para {ativo.adquirente}
                      {ativo.vinculoEntrePartes ? ` (${ativo.vinculoEntrePartes})` : ''}
                    </span>
                  </li>
                )}
                {ativo.proprietarioAtual && (
                  <li>
                    <span>Hoje em nome de {ativo.proprietarioAtual}</span>
                  </li>
                )}
                {!ativo.transmitente && !ativo.adquirente && !ativo.proprietarioAtual && (
                  <li>
                    <span>As partes não foram lançadas.</span>
                  </li>
                )}
              </ul>
              {ativo.valorDeclarado !== null && (
                <p className="imv-nada">
                  Declarado no ato <b className="imv-mono">{dinheiro(ativo.valorDeclarado)}</b> — o que consta da
                  escritura, não é avaliação.
                </p>
              )}
            </div>
          ) : (
            <div>
              <h5 className="imv-rot">Os poderes</h5>
              <ul className="imv-mini">
                <li>
                  <span>{ativo.poderes ?? 'Poderes não lançados.'}</span>
                </li>
                <li>
                  <span>
                    Prazo: {ativo.prazoDeValidade ?? 'não informado'} · substabelecer:{' '}
                    {ativo.podeSubstabelecer === null ? 'o documento não diz' : ativo.podeSubstabelecer ? 'sim' : 'não'}
                  </span>
                </li>
              </ul>
              <p className="imv-nada">⛔ Poder para praticar não é ato praticado.</p>
            </div>
          )}

          <div>
            <h5 className="imv-rot">Gravames</h5>
            {ativo.onus.length === 0 ? (
              <p className="imv-nada">Nenhum lançado.</p>
            ) : (
              <ul className="imv-mini">
                {ativo.onus.map((o) => (
                  <li key={o.id}>
                    <b>{o.valor !== null ? dinheiro(o.valor) : '—'}</b>
                    <span>
                      {o.natureza}
                      {o.credor ? ` · ${o.credor}` : ''}
                      {o.constituidoEm ? ` · desde ${emData(o.constituidoEm)}` : ''}
                    </span>
                    <em className={o.situacao ? 'sit' : 'sit na'}>{o.situacao ?? 'não apurada'}</em>
                    {ehMaster && (
                      <button type="button" className="botao-texto" onClick={() => setGravameAApagar(o)}>
                        apagar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {ehMaster && <FormularioDeOnus ativo={ativo} investigadoId={investigadoId} aoLancar={aoMudar} />}
          </div>

          {ativo.interpretacao && (
            <div className={`imv-interpretacao${ativo.aceita ? ' aceita' : ''}`}>
              <p>{ativo.interpretacao}</p>
              <p className="ajuda">
                {ativo.aceita ? 'Aceita — entra no documento entregue.' : 'Escrita pelo Hub, ainda não revista. Não entra no documento entregue.'}
                {ehMaster && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="botao-texto"
                      onClick={() =>
                        void (ativo.aceita
                          ? apiInvestigados.desfazerAceite(investigadoId, ativo.id)
                          : apiInvestigados.aceitarAtivo(investigadoId, ativo.id)
                        )
                          .then(() => {
                            aoMudar();
                            avisar(ativo.aceita ? 'Aceite desfeito.' : 'Aceita — entra no documento entregue.');
                          })
                          .catch(avisarErro)
                      }
                    >
                      {ativo.aceita ? 'desfazer o aceite' : 'aceitar'}
                    </button>
                  </>
                )}
              </p>
            </div>
          )}

          {ehMaster && (
            <div className="imv-detalhe-acoes">
              <button type="button" className="imv-bt p" onClick={() => aoCorrigir(ativo)}>
                <Traco d={['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4']} />
                Corrigir
              </button>
              <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar(true)}>
                <Traco d={['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13']} />
                Apagar
              </button>
            </div>
          )}
        </div>
      )}

      {aApagar && (
        <Confirmar
          titulo="Apagar este lançamento?"
          descricao={
            ativo.origem
              ? `"${ativo.descricao}" veio de consulta. Apagá-lo não devolve o que a consulta custou.`
              : `"${ativo.descricao}" será apagado, com os gravames lançados nele.`
          }
          aoConfirmar={async () => {
            await apiInvestigados.apagarAtivo(investigadoId, ativo.id);
            setAApagar(false);
            aoMudar();
          }}
          aoFechar={() => setAApagar(false)}
        />
      )}
      {gravameAApagar && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai deste ato agora, e não volta.`}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigadoId, ativo.id, gravameAApagar.id)
              .then(aoMudar)
              .catch(avisarErro)
          }
          aoFechar={() => setGravameAApagar(null)}
        />
      )}
    </article>
  );
}
